import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getShippingCosts } from "../../services/sales-api";
import { useAppSelector } from "../../store/hooks";
import "./styles/dashboard.css";

const filterMap = {
  Today: "today",
  Yesterday: "yesterday",
  "Last 7 Days": "last_7_days",
  "This Month": "this_month",
};

const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatAmount = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "--";
  }
  return Number(value).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatDate = (dateStr) => {
  if (!dateStr) return "--";
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
};

const ShippingCostList = ({
  dateRange,
  refreshKey,
  customStartDate,
  customEndDate,
}) => {
  const navigate = useNavigate();
  const { isPartsURL, isAccessoriesURL } = useAppSelector(
    (state) => state.domain
  );
  const [items, setItems] = useState([]);
  const [totalShippingCost, setTotalShippingCost] = useState(0);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const pageSize = 10;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const filterKey = `${dateRange}-${refreshKey}-${customStartDate}-${customEndDate}`;
  const prevFilterKey = useRef(filterKey);

  const primaryColor = isPartsURL
    ? "var(--primary-color-parts)"
    : isAccessoriesURL
    ? "var(--primary-color-acc)"
    : "var(--primary-color-mobile)";

  useEffect(() => {
    const fetchShippingCosts = async () => {
      let activePage = page;
      if (prevFilterKey.current !== filterKey) {
        prevFilterKey.current = filterKey;
        activePage = 1;
        if (page !== 1) {
          setPage(1);
          return;
        }
      }

      setLoading(true);
      setError(null);

      if (
        dateRange === "Custom Date" &&
        (!customStartDate || !customEndDate)
      ) {
        setItems([]);
        setTotalShippingCost(0);
        setCount(0);
        setLoading(false);
        return;
      }

      try {
        const params = {
          page: activePage,
          page_size: pageSize,
        };

        if (
          dateRange === "Custom Date" &&
          customStartDate &&
          customEndDate
        ) {
          params.start_date = formatLocalDate(customStartDate);
          params.end_date = formatLocalDate(customEndDate);
        } else {
          const filter = filterMap[dateRange];
          if (filter) {
            params.filter_date = filter;
          }
        }

        const response = await getShippingCosts(params);
        const list = response?.results ?? [];
        const totalCount = response?.count ?? 0;
        const total = response?.total_shipping_cost ?? 0;

        setItems(Array.isArray(list) ? list : []);
        setTotalShippingCost(total);
        setCount(totalCount);
      } catch (err) {
        console.error("Error fetching shipping costs:", err);
        const apiMessage =
          err?.response?.data?.errors ||
          err?.response?.data?.message ||
          "Could not load shipping costs";
        setError(
          typeof apiMessage === "string"
            ? apiMessage
            : "Could not load shipping costs"
        );
        setItems([]);
        setTotalShippingCost(0);
        setCount(0);
      } finally {
        setLoading(false);
      }
    };

    fetchShippingCosts();
  }, [
    dateRange,
    refreshKey,
    customStartDate,
    customEndDate,
    page,
    filterKey,
  ]);

  const totalPages = Math.max(1, Math.ceil(count / pageSize));
  const startItem = count === 0 ? 0 : (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, count);

  return (
    <div className="dashboard-card">
      <div className="dashboard-shipping-header">
        <div>
          <h3 className="dashboard-card-title">Shipping Costs</h3>
          <p className="dashboard-subtitle">
            Published sales with shipping charges for the selected period
          </p>
        </div>
        <div className="dashboard-shipping-total">
          <span className="dashboard-shipping-total-label">Total Shipping</span>
          <span
            className="dashboard-shipping-total-value"
            style={{ color: primaryColor }}
          >
            {loading ? "..." : `€ ${formatAmount(totalShippingCost)}`}
          </span>
        </div>
      </div>

      <div className="dashboard-shipping-table-wrap">
        <table className="dashboard-shipping-table">
          <thead>
            <tr>
              <th>Invoice ID</th>
              <th>Date</th>
              <th style={{ textAlign: "right" }}>Shipping Cost</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={3} className="dashboard-shipping-empty">
                  Loading...
                </td>
              </tr>
            )}
            {!loading && error && (
              <tr>
                <td colSpan={3} className="dashboard-shipping-empty error">
                  {error}
                </td>
              </tr>
            )}
            {!loading && !error && items.length === 0 && (
              <tr>
                <td colSpan={3} className="dashboard-shipping-empty">
                  {dateRange === "Custom Date" &&
                  (!customStartDate || !customEndDate)
                    ? "Select a start and end date to view shipping costs"
                    : "No shipping costs found for this period"}
                </td>
              </tr>
            )}
            {!loading &&
              !error &&
              items.map((item) => (
                <tr
                  key={item.sales_id || item.invoice_id}
                  className="dashboard-shipping-row"
                  onClick={() =>
                    item.sales_id && navigate(`/sales/view/${item.sales_id}`)
                  }
                >
                  <td>
                    <span
                      className="dashboard-shipping-invoice"
                      style={{ color: primaryColor }}
                    >
                      {item.invoice_id || "--"}
                    </span>
                  </td>
                  <td>{formatDate(item.created_at)}</td>
                  <td style={{ textAlign: "right", fontWeight: 600 }}>
                    € {formatAmount(item.shipping_cost)}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {!loading && !error && count > 0 && (
        <div className="dashboard-shipping-pagination">
          <span className="dashboard-shipping-pagination-info">
            Showing {startItem}-{endItem} of {count}
          </span>
          <div className="dashboard-shipping-pagination-controls">
            <button
              type="button"
              className="dashboard-btn-filter"
              disabled={page <= 1}
              onClick={() => setPage((prev) => prev - 1)}
            >
              Previous
            </button>
            <span className="dashboard-shipping-page-indicator">
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              className="dashboard-btn-filter"
              disabled={page >= totalPages}
              onClick={() => setPage((prev) => prev + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShippingCostList;
